import { GroupedEventsResponse, makeGroupEventsOffline, useEventsStore } from '@src/Store/eventsStore.ts'
import { useCredentialsStore } from '@src/Store/userStore.ts'
import { EmployeeEvents, WorkEventOutput } from '@src/httpClient/__generated__/Api.ts'
import { apiClient } from '@src/httpClient/api.ts'
import { useQueryWithOffline } from '@src/httpClient/utils/queryHelpers.ts'
import { listOfflineEvents } from '@src/offline/offline.ts'
import { sleep, useSyncStore } from '@src/offline/sync.ts'
import { getCurrentDateStr } from '@src/utils/formatDate.ts'
import { useNet } from '@src/utils/net.ts'
import React, { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

interface EventsFilter {
    recorded_at?: string
    record_range_after?: string
    record_range_before?: string
    search?: string
    branch?: string[]
    is_shifted?: boolean
}

/** Fetch all pages of eventsGrouped and return combined results */
const fetchAllPages = async (
    filter: EventsFilter,
    signal: AbortSignal
): Promise<{ results: EmployeeEvents[]; count: number }> => {
    const allResults: EmployeeEvents[] = []
    let page = 1
    let totalPages = 1

    while (page <= totalPages) {
        const searchStr = new URLSearchParams()
        if (filter.recorded_at) searchStr.set('recorded_at', filter.recorded_at)
        // record_range_* only drive the backend's is_shifted check; the event range itself is
        // recorded_at__gte/lte (see API-CONTRACT.md). A date-only upper bound parses as midnight
        // and drops the whole end day, so send end-of-day for it.
        if (filter.record_range_after) {
            searchStr.set('record_range_after', filter.record_range_after)
            searchStr.set('recorded_at__gte', filter.record_range_after)
        }
        if (filter.record_range_before) {
            searchStr.set('record_range_before', filter.record_range_before)
            searchStr.set('recorded_at__lte', `${filter.record_range_before}T23:59:59`)
        }
        if (filter.search) searchStr.set('search', filter.search)
        if (filter.branch && filter.branch.length > 0) searchStr.set('branch_ids__in', filter.branch.join(','))
        if (filter.is_shifted !== undefined) searchStr.set('is_shifted', String(filter.is_shifted))
        searchStr.set('page', String(page))

        const result = await apiClient.api.eventsGrouped(
            undefined,
            { search: searchStr.toString(), signal }
        )

        allResults.push(...(result.data.results ?? []))
        totalPages = result.data.total_pages
        page++
    }

    return { results: allResults, count: allResults.length }
}

export const EventsLayer: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [params] = useSearchParams()
    const employee = useCredentialsStore(x => x.employee)
    const setGroupedData = useEventsStore(x => x.setGroupedData)
    const setEventsIsLoading = useEventsStore(x => x.setIsLoading)
    const addRefetchAction = useEventsStore(x => x.addRefetchAction)
    const setError = useEventsStore(x => x.setError)
    const onlineStatus = useNet(x => x.online)
    const syncEvents = useSyncStore(x => x.syncEvents)

    const eventsFilter: EventsFilter = useMemo(() => {
        const date = params.get('date')
        const rangeAfter = params.get('record_range_after')
        const rangeBefore = params.get('record_range_before')
        const search = params.get('search') ?? undefined
        const branches = params.getAll('branches')
        const isShifted = params.get('is_shifted')

        const statusFilters = {
            ...(isShifted !== null && { is_shifted: isShifted === 'true' }),
        }

        if (date) {
            return { recorded_at: date, search, branch: branches, ...statusFilters }
        }
        if (rangeAfter) {
            return {
                record_range_after: rangeAfter,
                record_range_before: rangeBefore ?? undefined,
                search,
                branch: branches,
                ...statusFilters
            }
        }
        return { recorded_at: getCurrentDateStr(), search, branch: branches, ...statusFilters }
    }, [params])

    const offlineSearchStr = useMemo(() => {
        const base = new URLSearchParams()
        if (eventsFilter.recorded_at) base.set('record_range_after', eventsFilter.recorded_at)
        else if (eventsFilter.record_range_after) base.set('record_range_after', eventsFilter.record_range_after)
        return base.toString()
    }, [eventsFilter])

    const queryKey = useMemo(() => {
        const base = new URLSearchParams()
        if (eventsFilter.recorded_at) base.set('date', eventsFilter.recorded_at)
        if (eventsFilter.record_range_after) base.set('after', eventsFilter.record_range_after)
        if (eventsFilter.record_range_before) base.set('before', eventsFilter.record_range_before)
        if (eventsFilter.search) base.set('search', eventsFilter.search)
        if (eventsFilter?.branch && eventsFilter?.branch?.length > 0) base.set('branch', eventsFilter.branch.join(','))
        if (eventsFilter.is_shifted !== undefined) base.set('is_shifted', String(eventsFilter.is_shifted))
        return base
    }, [eventsFilter])

    const { data, refetch, isLoading, error, isFetching } = useQueryWithOffline<GroupedEventsResponse>(
        ['events-grouped', queryKey.toString()],
        // ONLINE
        async (_searchString, signal) => {
            const { results, count } = await fetchAllPages(eventsFilter, signal)
            return { results, count, __source: 'online' as const }
        },
        // OFFLINE
        async (_search, _signal) => {
            if (!employee) return { results: [], count: 0, __source: 'offline' as const }
            const flatEvents = await listOfflineEvents(offlineSearchStr, employee?.is_manager ? undefined : employee.id!)
            const grouped = makeGroupEventsOffline(flatEvents.results as WorkEventOutput[])
            const fallbackDate = eventsFilter.recorded_at ?? eventsFilter.record_range_after ?? getCurrentDateStr()
            const offlineResult: EmployeeEvents[] = [
                {
                    employee: { id: employee.id!, first_name: employee.first_name ?? '', last_name: employee.last_name ?? '' },
                    date: fallbackDate,
                    worktime_length: 0,
                    events: grouped
                }
            ]
            return { results: offlineResult, count: offlineResult.length, __source: 'offline' as const }
        },
        undefined,
        false
    )

    const syncing = { current: false, needRepeat: false }

    const runSyncOnce = async (events?: WorkEventOutput[]) => {
        if (syncing.current) {
            syncing.needRepeat = true
            return
        }
        syncing.current = true
        try {
            await syncEvents(events)
        } finally {
            syncing.current = false
            if (syncing.needRepeat) {
                syncing.needRepeat = false
                await runSyncOnce(events)
            }
        }
    }

    useEffect(() => {
        if (refetch) addRefetchAction(refetch)
    }, [refetch])

    useEffect(() => {
        setError(error || undefined)
    }, [error])
    useEffect(() => {
        sleep(2000).then(() => console.log('sleep'))
        setEventsIsLoading(isLoading)
    }, [isLoading, isFetching])

    useEffect(() => {
        if (!data?.results) return
        setGroupedData(data.results)
        if (onlineStatus && (data as any).__source === 'online') {
            const flatEvents: WorkEventOutput[] = []
            for (const ee of data.results) {
                for (const pair of ee.events) {
                    if (pair.event_in)
                        flatEvents.push({ ...pair.event_in, employee: ee.employee } as unknown as WorkEventOutput)
                    if (pair.event_out)
                        flatEvents.push({ ...pair.event_out, employee: ee.employee } as unknown as WorkEventOutput)
                }
            }
            runSyncOnce(flatEvents).catch(console.error)
        }
    }, [data, onlineStatus])

    useEffect(() => {
        if (onlineStatus) {
            runSyncOnce().catch(console.error)
        }
    }, [onlineStatus])

    return children
}
